import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Tag,
  TimePicker,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import { type CalendarKind, DATE, type EntryDraft } from './types';

interface FormValues {
  title: string;
  kindId: number;
  dates: [Dayjs, Dayjs];
  times?: [Dayjs, Dayjs] | null;
  description?: string;
}

interface Props {
  // null = closed.
  draft: EntryDraft | null;
  kinds: CalendarKind[];
  onClose: () => void;
  onSaved: () => void;
}

// Adds, edits or removes a Calendar entry (edit level only). Its kind decides
// whether it is whole days or has a start and end time.
export default function EntryDrawer({ draft, kinds, onClose, onSaved }: Props) {
  const { runQuery } = useScreen();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const entry = draft && 'entry' in draft ? draft.entry : null;

  const kindId = Form.useWatch('kindId', form);
  const kind = kinds.find((k) => k.id === kindId);

  useEffect(() => {
    if (!draft) return;
    if (entry) {
      form.setFieldsValue({
        title: entry.title,
        kindId: entry.kind_id,
        dates: [dayjs(entry.start_date), dayjs(entry.end_date)],
        times: entry.start_time
          ? [dayjs(entry.start_time, 'HH:mm'), dayjs(entry.end_time ?? entry.start_time, 'HH:mm')]
          : null,
        description: entry.description ?? '',
      });
    } else if ('startDate' in draft) {
      form.setFieldsValue({
        title: '',
        kindId: kinds[0]?.id,
        dates: [dayjs(draft.startDate), dayjs(draft.endDate)],
        times: null,
        description: '',
      });
    }
  }, [draft, entry, kinds, form]);

  const save = async () => {
    const values = await form.validateFields();
    // The kind decides whether the entry has a time (CONTEXT.md: Entry kind).
    const timed = kind?.has_time && values.times;
    setSaving(true);
    try {
      await runQuery('calendar_entry_save', {
        ...(entry ? { id: entry.id } : {}),
        title: values.title.trim(),
        kindId: values.kindId,
        startDate: values.dates[0].format(DATE),
        endDate: values.dates[1].format(DATE),
        startTime: timed ? values.times?.[0].format('HH:mm') : null,
        endTime: timed ? values.times?.[1].format('HH:mm') : null,
        description: values.description?.trim() || null,
      });
      message.success(entry ? 'Хадгаллаа' : 'Нэмэгдлээ');
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!entry) return;
    setDeleting(true);
    try {
      await runQuery('calendar_entry_delete', { id: entry.id });
      message.success('Устгалаа');
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const title = entry ? 'Засах' : 'Шинээр нэмэх';

  return (
    <Drawer
      open={!!draft}
      onClose={onClose}
      title={title}
      size={520}
      destroyOnHidden
      extra={
        <Button type="primary" loading={saving} onClick={save}>
          Хадгалах
        </Button>
      }
      footer={
        entry && (
          <Popconfirm
            title="Устгах уу?"
            description={`"${entry.title}"`}
            okText="Устгах"
            cancelText="Болих"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
          >
            <Button danger type="text" icon={<DeleteOutlined />} loading={deleting}>
              Устгах
            </Button>
          </Popconfirm>
        )
      }
    >
      <Form form={form} layout="vertical" requiredMark onFinish={save}>
        <Form.Item
          name="title"
          rules={[{ required: true, whitespace: true, message: 'Гарчгаа оруулна уу' }]}
        >
          <Input
            size="large"
            variant="underlined"
            placeholder="Гарчиг нэмэх"
            autoFocus
            className="text-lg!"
          />
        </Form.Item>

        <Form.Item
          name="kindId"
          label="Төрөл"
          rules={[{ required: true, message: 'Төрлөө сонгоно уу' }]}
        >
          <Select
            options={kinds.map((k) => ({
              value: k.id,
              label: (
                <span className="flex items-center gap-2">
                  <span className="size-3 shrink-0" style={{ backgroundColor: k.color }} />
                  {k.name}
                  {(k.no_lessons || k.has_time) && (
                    <Tag bordered={false} className="ml-auto! text-[11px]!">
                      {k.no_lessons ? 'Хичээлгүй' : 'Цагтай'}
                    </Tag>
                  )}
                </span>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="dates"
          label="Өдөр"
          rules={[{ required: true, message: 'Өдрөө сонгоно уу' }]}
        >
          <DatePicker.RangePicker className="w-full" format="YYYY.MM.DD" allowClear={false} />
        </Form.Item>

        {kind?.no_lessons && (
          <div className="-mt-2 mb-4 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            Хичээлгүй өдөр — бүтэн өдрөөр бүртгэгдэнэ.
          </div>
        )}
        {kind?.has_time && (
          <Form.Item
            name="times"
            label="Цаг"
            rules={[{ required: true, message: 'Цагаа сонгоно уу' }]}
          >
            <TimePicker.RangePicker
              className="w-full"
              format="HH:mm"
              minuteStep={5}
              placeholder={['Эхлэх цаг', 'Дуусах цаг']}
            />
          </Form.Item>
        )}

        <Form.Item name="description" label="Тайлбар">
          <Input.TextArea rows={3} placeholder="Нэмэлт мэдээлэл" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
